import { ActionTree } from 'vuex'
import { Logger } from '@vue-storefront/core/lib/logger'
import * as types from './mutation-types'
import NetsuiteData, {
  CustomerServiceRequestPayload,
  OrderFilters,
  OrderSummaryFilters
} from '../types/NetsuiteData'
import { netsuiteFetch, netsuiteFetchBlob } from '../helpers/api'

const firstOrNull = (data: any) => {
  if (Array.isArray(data)) {
    return data.length ? data[0] : null
  }
  return data || null
}

const lowerKeys = (value: any): any => {
  if (Array.isArray(value)) return value.map(lowerKeys)
  if (!value || typeof value !== 'object') return value
  return Object.keys(value).reduce((acc: any, key) => {
    acc[String(key).toLowerCase()] = lowerKeys(value[key])
    return acc
  }, {})
}

const normalizeStatusQuery = (query: Record<string, any>) => {
  if (Array.isArray(query.status)) {
    query.status = query.status.join(',')
  }
  return query
}

/** Tracks the in-flight order-items request so stale responses are ignored. */
let activeOrderItemsRequestId: string | null = null

export const actions: ActionTree<NetsuiteData, any> = {
  async fetchCustomer ({ commit }) {
    commit(types.NETSUITE_LOADING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const data = await netsuiteFetch('/customers/me', undefined, {
        includeEmployee: 1
      })

      const customer = firstOrNull(data)

      commit(types.NETSUITE_CUSTOMER, customer)
      return customer
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to load NetSuite customer')
      throw err
    } finally {
      commit(types.NETSUITE_LOADING, false)
    }
  },

  async fetchOrders ({ commit }, filters: OrderFilters = {}) {
    commit(types.NETSUITE_LOADING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const query = normalizeStatusQuery({ ...filters })
      const data = await netsuiteFetch('/customers/me/orders', { method: 'GET' }, query)
      const orders = Array.isArray(data) ? data : []

      commit(types.NETSUITE_ORDERS, orders)
      return orders
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to load NetSuite orders')
      throw err
    } finally {
      commit(types.NETSUITE_LOADING, false)
    }
  },

  async fetchOrderSummaries ({ commit }, filters: OrderSummaryFilters = {}) {
    commit(types.NETSUITE_LOADING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const query = normalizeStatusQuery({ ...filters })
      const data = await netsuiteFetch('/customers/me/orders/summary', { method: 'GET' }, query)
      const normalized = lowerKeys(data || {})
      const page = {
        orders: Array.isArray(normalized.orders) ? normalized.orders : [],
        pagination: normalized.pagination || { limit: 0, offset: 0, count: 0 }
      }

      commit(types.NETSUITE_ORDER_SUMMARIES, page)
      return page
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to load NetSuite order summaries')
      throw err
    } finally {
      commit(types.NETSUITE_LOADING, false)
    }
  },

  async fetchOrder ({ commit }, etailOrderId: string | number) {
    if (!etailOrderId) {
      throw new Error('etailOrderId is required')
    }

    commit(types.NETSUITE_LOADING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const data = await netsuiteFetch(`/customers/me/orders/${etailOrderId}`)
      const order = firstOrNull(data)
      commit(types.NETSUITE_CURRENT_ORDER, order)
      return order
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to load NetSuite order')
      throw err
    } finally {
      commit(types.NETSUITE_LOADING, false)
    }
  },

  async fetchOrderItems ({ commit }, transactionId: string | number) {
    if (!transactionId) {
      throw new Error('transactionId is required')
    }

    const requestId = String(transactionId)
    activeOrderItemsRequestId = requestId
    commit(types.NETSUITE_ORDER_ITEMS, null)
    commit(types.NETSUITE_LOADING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const data = await netsuiteFetch(`/customers/me/orders/${transactionId}/items`)
      if (activeOrderItemsRequestId !== requestId) {
        return []
      }
      const items = lowerKeys(Array.isArray(data) ? data : [])
      commit(types.NETSUITE_ORDER_ITEMS, items)
      return items
    } catch (err) {
      if (activeOrderItemsRequestId !== requestId) {
        return []
      }
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to load NetSuite order items')
      throw err
    } finally {
      if (activeOrderItemsRequestId === requestId) {
        commit(types.NETSUITE_LOADING, false)
      }
    }
  },

  clearOrderItems ({ commit }) {
    activeOrderItemsRequestId = null
    commit(types.NETSUITE_ORDER_ITEMS, null)
    commit(types.NETSUITE_LOADING, false)
    commit(types.NETSUITE_ERROR, null)
  },

  /**
   * Download full order-item history as CSV.
   * Optional startDate / endDate (ISO or YYYY-MM-DD); omit both for the full history.
   */
  async downloadOrderItemsCsv (_context, filters: { startDate?: string, endDate?: string } = {}) {
    const query: Record<string, string> = {}
    if (filters.startDate) query.startDate = filters.startDate
    if (filters.endDate) query.endDate = filters.endDate

    try {
      const { blob, filename } = await netsuiteFetchBlob(
        '/customers/me/orders/items',
        { method: 'GET' },
        query
      )
      return {
        blob,
        filename: filename || 'order-items.csv'
      }
    } catch (err) {
      Logger.error(err, 'netsuite')()
      throw err
    }
  },

  async fetchTracking ({ commit }, etailOrderId: string | number) {
    if (!etailOrderId) {
      throw new Error('etailOrderId is required')
    }

    commit(types.NETSUITE_LOADING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const data = await netsuiteFetch(`/customers/me/orders/${etailOrderId}/tracking`)
      const tracking = Array.isArray(data) ? data : []
      commit(types.NETSUITE_TRACKING, tracking)
      return tracking
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to load tracking')
      throw err
    } finally {
      commit(types.NETSUITE_LOADING, false)
    }
  },

  async submitCustomerServiceRequest ({ commit }, payload: CustomerServiceRequestPayload) {
    if (!payload || !payload.message) {
      throw new Error('message is required')
    }

    commit(types.NETSUITE_SUBMITTING, true)
    commit(types.NETSUITE_ERROR, null)

    try {
      const data = await netsuiteFetch('/customers/me/customer-service-request', {
        method: 'POST',
        body: JSON.stringify({
          message: payload.message,
          issue: payload.issue,
          subject: payload.subject
        })
      })
      return data
    } catch (err) {
      Logger.error(err, 'netsuite')()
      commit(types.NETSUITE_ERROR, err.message || 'Failed to submit customer service request')
      throw err
    } finally {
      commit(types.NETSUITE_SUBMITTING, false)
    }
  },

  clear ({ commit }) {
    commit(types.NETSUITE_CLEAR)
  }
}
